import React, { useMemo } from "react";
import { SUPERMARKETS_BY_ID } from "../../data/supermarkets";
import { computeAffordability } from "../../util/affordability";
import {
  dominantGroceryTier,
  estimateGroceries,
} from "../../util/costEstimates";
import { estimateCommuteCost } from "../../util/tflFareHeuristic";
import { CostOfLivingFormState, MyDayPlan } from "./types";

export interface CostOfLivingSummaryProps {
  price: number;
  rentFrequency: string;
  deposit: number | null;
  postcode: string;
  bedrooms: number | null;
  internalArea: number | null;
  description: string[];
  form: CostOfLivingFormState;
  plan: MyDayPlan | null;
}

const formatGbp = (n: number) =>
  `£${n.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;

const formatGbpRange = (min: number, max: number) =>
  min === max ? formatGbp(min) : `${formatGbp(min)}–${formatGbp(max)}`;

const CostOfLivingSummary: React.FC<CostOfLivingSummaryProps> = ({
  price,
  rentFrequency,
  deposit,
  postcode,
  bedrooms,
  internalArea,
  description,
  form,
  plan,
}) => {
  const monthlyIncome = form.incomeIsAnnual
    ? form.householdIncome / 12
    : form.householdIncome;

  const affordability = useMemo(
    () =>
      computeAffordability({
        price,
        rentFrequency,
        deposit,
        householdIncomeMonthly: monthlyIncome,
        occupants: form.occupants,
        includeBills: form.includeBills,
        postcode,
        bedrooms,
        internalArea,
        description,
        includeTvLicence: form.includeTvLicence,
        includeContentsInsurance: form.includeContentsInsurance,
      }),
    [
      price,
      rentFrequency,
      deposit,
      monthlyIncome,
      form,
      postcode,
      bedrooms,
      internalArea,
      description,
    ]
  );

  const tiers = form.selectedSupermarketIds
    .map((id) => SUPERMARKETS_BY_ID[id]?.tier)
    .filter(Boolean);
  const groceryTier = dominantGroceryTier(tiers);
  const groceries = estimateGroceries(
    groceryTier,
    form.occupants,
    form.shopsPerWeek
  );
  const commute = estimateCommuteCost(form.commuteMode, form.daysPerWeek);

  const monthlyLivingMin =
    affordability.monthlyRent +
    affordability.monthlyBills +
    commute.monthlyMin +
    groceries.monthlyMin;
  const monthlyLivingMax =
    affordability.monthlyRent +
    affordability.monthlyBills +
    commute.monthlyMax +
    groceries.monthlyMax;

  const dailyCommuteMins = plan
    ? Math.round(plan.totalDurationSeconds / 60)
    : null;

  return (
    <div className="bg-bg-white border border-solid border-[rgba(0,0,0,0.05)] shadow-soft p-6">
      <div className="font-manrope text-xs uppercase tracking-wider text-text-slate mb-2">
        Summary
      </div>
      <h3 className="font-teko text-2xl text-text-obsidian mb-4">
        Estimated cost of living
      </h3>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="p-4 bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.06)]">
          <div className="text-xs uppercase tracking-wider text-text-slate">
            Monthly total
          </div>
          <div className="font-teko text-2xl text-text-obsidian mt-1">
            {formatGbpRange(monthlyLivingMin, monthlyLivingMax)}
          </div>
          <div className="text-xs text-text-grey mt-1">Rent + bills + commute + groceries</div>
        </div>
        <div className="p-4 bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.06)]">
          <div className="text-xs uppercase tracking-wider text-text-slate">
            Weekly groceries
          </div>
          <div className="font-teko text-2xl text-text-obsidian mt-1">
            {formatGbpRange(groceries.weeklyMin, groceries.weeklyMax)}
          </div>
          <div className="text-xs text-text-grey mt-1 capitalize">
            {groceryTier} tier · {form.occupants} occupant(s)
          </div>
        </div>
        <div className="p-4 bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.06)]">
          <div className="text-xs uppercase tracking-wider text-text-slate">
            Monthly commute
          </div>
          <div className="font-teko text-2xl text-text-obsidian mt-1">
            {formatGbpRange(commute.monthlyMin, commute.monthlyMax)}
          </div>
          <div className="text-xs text-text-grey mt-1">
            {form.daysPerWeek} days/week · {form.commuteMode}
          </div>
        </div>
        <div className="p-4 bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.06)]">
          <div className="text-xs uppercase tracking-wider text-text-slate">
            Daily travel time
          </div>
          <div className="font-teko text-2xl text-text-obsidian mt-1">
            {dailyCommuteMins != null ? `${dailyCommuteMins} min` : "—"}
          </div>
          <div className="text-xs text-text-grey mt-1">From My Day map routes</div>
        </div>
      </div>

      {plan?.primarySupermarket && (
        <div className="mb-6 p-4 border border-solid border-[rgba(0,0,0,0.06)]">
          <div className="font-manrope text-xs uppercase tracking-wider text-text-slate mb-2">
            Nearest preferred supermarket
          </div>
          <div className="font-medium">{plan.primarySupermarket.name}</div>
          {plan.primarySupermarket.address && (
            <div className="text-sm text-text-grey">
              {plan.primarySupermarket.address}
            </div>
          )}
          {plan.supermarkets.length > 1 && (
            <div className="text-xs text-text-grey mt-2">
              Also nearby:{" "}
              {plan.supermarkets
                .slice(1, 4)
                .map((s) => s.name)
                .join(", ")}
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-text-grey">
        Figures above are indicative ranges. Commute fares use simplified bands,
        not live TfL pricing.
      </p>
    </div>
  );
};

export default CostOfLivingSummary;
